import { motion } from "framer-motion";
import { BriefcaseBusiness, CalendarDays, MapPin } from "lucide-react";
import { experienceData } from "./data";

type ExperienceProps = {
  id?: string;
  mobile?: boolean;
};

export function Experience({ id = "experience", mobile = false }: ExperienceProps) {
  return (
    <section
      id={id}
      className={mobile ? "w-full px-2.5 mt-20" : "py-20 px-6 md:px-12 lg:px-16"}
    >
      <div className="w-full max-w-7xl mx-auto">
        <div className="mb-10 md:mb-14 text-center">
          <h2 className="font-body text-lg font-light text-foreground mb-4">
            EXPERIENCE
          </h2>
          <p className="font-heading text-3xl md:text-5xl text-foreground">
            Professional Journey
          </p>
          <p className="font-body text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl mx-auto mt-5">
            5+ years building scalable web applications, custom CRM/CMS solutions,
            and intelligent automation systems.
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-[0.72fr_2fr] lg:gap-6">
          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.5 }}
            className="relative overflow-hidden rounded-[2rem] border border-border bg-card p-7 md:p-8"
          >
            <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-foreground/[0.04] blur-2xl" />
            <BriefcaseBusiness className="h-9 w-9 text-foreground" strokeWidth={1.5} />
            <p className="font-heading text-5xl md:text-6xl text-foreground mt-8">5+</p>
            <p className="font-heading text-lg text-foreground mt-2">Years of experience</p>
            <p className="font-body text-sm text-muted-foreground leading-relaxed mt-4">
              Full-stack development, AI automation, system integrations, and
              production-ready digital solutions.
            </p>
            <div className="mt-8 pt-6 border-t border-border">
              <p className="font-body text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Current company
              </p>
              <p className="font-heading text-base text-foreground mt-2">
                Generation Next Technology
              </p>
            </div>
          </motion.aside>

          <div className="relative rounded-[2rem] border border-border bg-card px-5 py-3 md:px-8 md:py-5">
            <div className="absolute left-[2.15rem] md:left-[3.15rem] top-10 bottom-10 w-px bg-border" />
            {experienceData.map((experience, index) => (
              <motion.article
                key={experience.company}
                initial={{ opacity: 0, x: 18 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
                className="relative grid grid-cols-[1.5rem_1fr] gap-4 py-6 md:grid-cols-[2rem_1fr] md:gap-6 md:py-7 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-border"
              >
                <div
                  className={`relative z-10 mt-1 h-4 w-4 rounded-full border-4 md:ml-1 ${
                    experience.current
                      ? "border-foreground bg-background shadow-[0_0_0_5px] shadow-foreground/10"
                      : "border-muted-foreground/60 bg-card"
                  }`}
                />

                <div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-heading text-base md:text-lg text-foreground">
                          {experience.roles[0].title}
                        </h3>
                        {experience.current && (
                          <span className="rounded-full border border-foreground/20 bg-foreground/[0.06] px-2.5 py-1 font-body text-[10px] uppercase tracking-[0.16em] text-foreground">
                            Current
                          </span>
                        )}
                      </div>
                      <p className="font-body text-sm text-foreground/85 mt-1">
                        {experience.company} · {experience.employmentType}
                      </p>
                    </div>
                    <span className="font-body text-xs text-muted-foreground whitespace-nowrap sm:pt-1">
                      {experience.duration}
                    </span>
                  </div>

                  <div className="flex flex-col gap-1.5 mt-3 text-muted-foreground sm:flex-row sm:flex-wrap sm:gap-x-5">
                    <span className="inline-flex items-center gap-1.5 font-body text-xs">
                      <CalendarDays className="h-3.5 w-3.5" />
                      {experience.period}
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-body text-xs">
                      <MapPin className="h-3.5 w-3.5" />
                      {experience.location}
                    </span>
                  </div>

                  {experience.roles.length > 1 && (
                    <div className="mt-5 space-y-3 border-l border-border pl-4">
                      {experience.roles.map((role) => (
                        <div key={role.title}>
                          <p className="font-body text-sm font-medium text-foreground">
                            {role.title}
                          </p>
                          <p className="font-body text-xs text-muted-foreground mt-0.5">
                            {role.period}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
